import { getAllConfigs } from '../services/configService';
import {
  hasMarketingConsent,
  isAdminPath
} from './analyticsConsent';

// 15–16 digit Meta Pixel ids only; anything else must never initialize.
const PIXEL_ID_RE = /^\d{15,16}$/;

let initPromise = null;
let pixelId = null;
let pixelInitialized = false;
// Logged-in user for Advanced Matching (email + external_id hashed).
let matchingUser = null;

export const isValidMetaPixelId = (id) => PIXEL_ID_RE.test(String(id || '').trim());

/** Shared browser + CAPI purchase event_id (must match exactly). */
export const buildPurchaseEventId = (orderId) => `purchase_${orderId}`;

/** Same normalisation as backend metaCapiService.hashEmail / hashExternalId. */
export const normalizeEmailForMeta = (email) => String(email ?? '').trim().toLowerCase();
export const normalizeExternalIdForMeta = (userId) => String(userId);

const getCryptoSubtle = () => {
  // Prefer window for CRA/eslint (globalThis is not in browser env globals).
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    return window.crypto.subtle;
  }
  if (typeof global !== 'undefined' && global.crypto && global.crypto.subtle) {
    return global.crypto.subtle;
  }
  return null;
};

export const sha256Hex = async (value) => {
  if (value == null || value === '') {
    return null;
  }
  const str = String(value);
  try {
    const subtle = getCryptoSubtle();
    if (subtle && typeof TextEncoder !== 'undefined') {
      const data = new TextEncoder().encode(str);
      const digest = await subtle.digest('SHA-256', data);
      return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    }
  } catch (error) {
    console.error('metaPixel sha256Hex SubtleCrypto failed:', error);
  }

  // Jest/jsdom may lack SubtleCrypto; node createHash keeps tests honest without shipping a polyfill.
  try {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
      // eslint-disable-next-line global-require
      const nodeCrypto = require('crypto');
      return nodeCrypto.createHash('sha256').update(str, 'utf8').digest('hex');
    }
  } catch (error) {
    console.error('metaPixel sha256Hex node fallback failed:', error);
  }

  return null;
};

export const buildAdvancedMatchingParams = async (user) => {
  if (!user) {
    return {};
  }
  const params = {};
  if (user.email) {
    const em = await sha256Hex(normalizeEmailForMeta(user.email));
    if (em) {
      params.em = em;
    }
  }
  const uid = user.id ?? user.user_id;
  if (uid != null && uid !== '') {
    const externalId = await sha256Hex(normalizeExternalIdForMeta(uid));
    if (externalId) {
      params.external_id = externalId;
    }
  }
  return params;
};

export const getMetaCookie = (name) => {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const injectFbevents = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  if (window.fbq) {
    return;
  }

  // Standard Meta stub (module-friendly; no document.write).
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    // jsdom / empty head may have no existing script nodes
    if (s && s.parentNode) {
      s.parentNode.insertBefore(t, s);
    } else {
      (b.head || b.documentElement).appendChild(t);
    }
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
};

const doInitialize = async () => {
  try {
    if (isAdminPath()) {
      return false;
    }

    if (!hasMarketingConsent()) {
      return false;
    }

    const configResponse = await getAllConfigs();
    const configuredId = configResponse?.data?.metaPixelId;
    if (!configResponse?.success || !isValidMetaPixelId(configuredId)) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Meta Pixel not initialized: missing or invalid metaPixelId in config');
      }
      return false;
    }

    pixelId = String(configuredId).trim();
    injectFbevents();

    if (typeof window.fbq !== 'function') {
      console.warn('Meta Pixel not initialized: fbq stub missing after inject');
      return false;
    }

    await runFbqInit();
    // No PageView here — AnalyticsRouter owns all PageViews (landing + SPA nav)
    // so we never double-fire on first paint.
    pixelInitialized = true;
    return true;
  } catch (error) {
    console.error('Error initializing Meta Pixel:', error);
    return false;
  }
};

const runFbqInit = async () => {
  const advancedMatching = await buildAdvancedMatchingParams(matchingUser);
  if (Object.keys(advancedMatching).length > 0) {
    // Hashed em + external_id only — never raw PII.
    window.fbq('init', pixelId, advancedMatching);
  } else {
    window.fbq('init', pixelId);
  }
};

/**
 * Update Advanced Matching after login/register (or clear on logout).
 * fbq('init') may be called again with user data when the session becomes known.
 */
export const setMetaMatchingUser = async (user) => {
  try {
    matchingUser = user
      ? {
          email: user.email || null,
          id: user.id ?? user.user_id ?? null
        }
      : null;

    if (!hasMarketingConsent() || isAdminPath()) {
      return false;
    }

    if (!pixelInitialized || typeof window.fbq !== 'function' || !pixelId) {
      // Init path will pick up matchingUser when marketing allows.
      return initializeMetaPixelIfAllowed();
    }

    await runFbqInit();
    return true;
  } catch (error) {
    console.error('metaPixel setMetaMatchingUser failed:', error);
    return false;
  }
};

/**
 * Marketing-only entry path. Must NOT go through initializeAnalyticsIfAllowed —
 * that returns early without analytics consent.
 */
export const initializeMetaPixelIfAllowed = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve(false);
  }

  if (!hasMarketingConsent() || isAdminPath()) {
    return Promise.resolve(false);
  }

  if (pixelInitialized && typeof window.fbq === 'function') {
    return Promise.resolve(true);
  }

  if (!initPromise) {
    initPromise = doInitialize().then((ok) => {
      if (!ok) {
        initPromise = null;
      }
      return ok;
    });
  }

  return initPromise;
};

export const applyConsentToMetaPixel = (consent) => {
  if (consent?.marketing) {
    initializeMetaPixelIfAllowed();
  }
};

const isMetaAllowed = (pathname) => {
  if (typeof window === 'undefined') {
    return false;
  }
  if (!hasMarketingConsent() || isAdminPath(pathname)) {
    return false;
  }
  return pixelInitialized && typeof window.fbq === 'function';
};

/**
 * SPA PageView — call from AnalyticsRouter on every route change (including first mount).
 * Awaits marketing-gated init so the landing hit is not lost to a race with App.js.
 */
export const trackMetaPageView = async (pathname) => {
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    if (!hasMarketingConsent() || isAdminPath(pathname)) {
      return false;
    }

    await initializeMetaPixelIfAllowed();

    if (!isMetaAllowed(pathname)) {
      return false;
    }

    if (process.env.NODE_ENV !== 'production') {
      console.debug('[Meta]', 'PageView', pathname || window.location.pathname);
    }

    window.fbq('track', 'PageView');
    return true;
  } catch (error) {
    console.error('metaPixel trackMetaPageView failed:', error);
    return false;
  }
};

/**
 * Fire a Meta event.
 * - Standard events: fbq('track', ...)
 * - Custom events (e.g. ViewCategory): options.custom === true → fbq('trackCustom', ...)
 * options.eventID is required for Purchase dedup with CAPI.
 */
export const trackMetaEvent = (eventName, params = {}, options = {}) => {
  try {
    if (!isMetaAllowed()) {
      return false;
    }

    if (process.env.NODE_ENV !== 'production') {
      console.debug('[Meta]', eventName, params, options);
    }

    const method = options.custom ? 'trackCustom' : 'track';
    if (options.eventID) {
      window.fbq(method, eventName, params, { eventID: options.eventID });
    } else {
      window.fbq(method, eventName, params);
    }
    return true;
  } catch (error) {
    console.error(`metaPixel trackMetaEvent(${eventName}) failed:`, error);
    return false;
  }
};

/** Convenience for non-standard Meta events (ViewCategory, etc.). */
export const trackMetaCustomEvent = (eventName, params = {}, options = {}) =>
  trackMetaEvent(eventName, params, { ...options, custom: true });

/** Map GA4 ecommerce events → Meta standard events (shared instrumentation layer). */
export const GA4_TO_META_EVENT = {
  view_item: 'ViewContent',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  add_payment_info: 'AddPaymentInfo',
  purchase: 'Purchase'
};

export const toMetaPayloadFromGa4Items = (items = [], value) => {
  const safeItems = Array.isArray(items) ? items : [];
  const contentIds = safeItems.map((item) => String(item.item_id)).filter(Boolean);
  const contents = safeItems
    .filter((item) => item.item_id !== '' && item.item_id != null)
    .map((item) => ({
      id: String(item.item_id),
      quantity: Number(item.quantity) || 1,
      item_price: Number(item.price) || 0
    }));

  return {
    content_ids: contentIds,
    content_type: 'product',
    value: Number.isFinite(Number(value)) ? Number(value) : 0,
    currency: 'EUR',
    contents
  };
};

export const trackMetaFromGa4 = (ga4EventName, { items, value, eventID } = {}) => {
  const metaName = GA4_TO_META_EVENT[ga4EventName];
  if (!metaName) {
    return false;
  }
  const payload = toMetaPayloadFromGa4Items(items, value);
  if (payload.content_ids.length === 0 && ga4EventName !== 'purchase') {
    // Purchase may still send with empty contents if items failed mapping — caller guards.
  }
  return trackMetaEvent(metaName, payload, eventID ? { eventID } : {});
};

export const __testing = {
  __resetMetaPixel: () => {
    initPromise = null;
    pixelId = null;
    pixelInitialized = false;
    matchingUser = null;
    if (typeof window !== 'undefined') {
      try {
        delete window.fbq;
        delete window._fbq;
      } catch (e) {
        window.fbq = undefined;
        window._fbq = undefined;
      }
    }
  },
  __setInitialized: (id = '123456789012345', fbqFn) => {
    pixelId = id;
    pixelInitialized = true;
    if (typeof window !== 'undefined' && typeof fbqFn === 'function') {
      window.fbq = fbqFn;
    }
  },
  __getState: () => ({ pixelId, pixelInitialized, matchingUser }),
  __setMatchingUser: (user) => {
    matchingUser = user;
  }
};
